import { useState, useEffect } from 'react';
import { base44 } from '@/api/base44Client';

/**
 * Hook to dynamically fetch the base price for a sauna model
 * @param {string} saunaName - The name of the sauna model (e.g., 'LYKKE UMI', 'LYKKE MORI')
 * @param {number} fallbackPrice - Fallback price if the sauna is not found
 * @returns {object} { basePrice, loading, error }
 */
export function useSaunaBasePrice(saunaId, fallbackPrice = 0) {
  const [basePrice, setBasePrice] = useState(fallbackPrice);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPrice = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Query the SaunaPrice entity by saunaId
        const prices = await base44.entities.SaunaPrice.filter({ saunaId });
        
        if (prices.length > 0) {
          setBasePrice(prices[0].price);
        } else {
          setError(`Price for sauna "${saunaId}" not found`);
          setBasePrice(fallbackPrice);
        }
      } catch (err) {
        console.error(`Error loading price for sauna "${saunaId}":`, err);
        setError(err.message);
        setBasePrice(fallbackPrice);
      } finally {
        setLoading(false);
      }
    };

    loadPrice();

    // Subscribe to SaunaPrice changes for real-time updates
    const unsubscribe = base44.entities.SaunaPrice.subscribe(() => {
      loadPrice();
    });

    return () => {
      unsubscribe();
    };
  }, [saunaId, fallbackPrice]);

  return { basePrice, loading, error };
}